import { createContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ResultOf } from '@graphql-typed-document-node/core';

import { invariant } from '@e-socna/utils';
import { graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY, NULL_FUNCTION } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

export const NotificationsContextProvider_Notification_Fragment = graphql(`
  fragment NotificationsContextProvider_Notification on Notification {
    _id
    primaryText
    secondaryText
    createdAt
    hasBeenRead
    chip {
      color
      icon
    }
  }
`);

export type NotificationsContextProvider_Notification = ResultOf<
  typeof NotificationsContextProvider_Notification_Fragment
>;

interface NotificationsData {
  readonly notifications: readonly NotificationsContextProvider_Notification[];
  readonly getUnreadNotifications: () => readonly NotificationsContextProvider_Notification[];
  readonly addNotification: (
    notification: NotificationsContextProvider_Notification,
  ) => void;
  readonly removeNotification: (id: Id) => void;
  readonly markNotificationAsRead: (id: Id) => void;
  readonly markAllNotificationsAsRead: () => void;
}

const NotificationsContext = createContext<NotificationsData>({
  notifications: EMPTY_ARRAY,
  getUnreadNotifications: () => EMPTY_ARRAY,
  addNotification: NULL_FUNCTION,
  removeNotification: NULL_FUNCTION,
  markNotificationAsRead: NULL_FUNCTION,
  markAllNotificationsAsRead: NULL_FUNCTION,
});

interface Props {
  readonly children: ReactNode;
}

// COMBAK : Notification type is only used in front-end (not plugged - mutation or queries - to the back-end)
// TODO : Once we use graphql query & mutations, we should use its type instead of Notification type.
const NotificationsContextProvider = ({ children }: Readonly<Props>) => {
  const [notifications, setNotifications] =
    useState<readonly NotificationsContextProvider_Notification[]>(EMPTY_ARRAY);

  const value = useMemo(
    () => ({
      notifications,
      getUnreadNotifications: () =>
        notifications.filter((notification) => !notification.hasBeenRead),
      addNotification: (
        notification: NotificationsContextProvider_Notification,
      ) => {
        invariant(
          !notifications.find((n) => n._id === notification._id),
          `Notification with id ${notification._id} already exists`,
        );
        setNotifications((notifications) => [...notifications, notification]);
      },
      removeNotification: (id: Id) => {
        invariant(
          !!notifications.find((n) => n._id === id),
          `Notification with id ${id} does not exists`,
        );
        setNotifications((notifications) =>
          notifications.filter((notification) => notification._id !== id),
        );
      },
      markNotificationAsRead: (id: Id) => {
        invariant(
          !!notifications.find((n) => n._id === id),
          `Notification with id ${id} does not exists`,
        );
        setNotifications((notifications) =>
          notifications.map((notification) =>
            notification._id === id
              ? { ...notification, hasBeenRead: true }
              : notification,
          ),
        );
      },
      markAllNotificationsAsRead: () => {
        setNotifications((notifications) =>
          notifications.map((notification) => ({
            ...notification,
            hasBeenRead: true,
          })),
        );
      },
    }),
    [notifications],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export { NotificationsContext, NotificationsContextProvider };
